import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle2, EllipsisVertical, KeyRound, Pencil, ShieldCheck, Trash2, Unlock, UserRound } from 'lucide-react'
import { toast } from 'sonner'
import { useEffect, useMemo, useState, type FormEvent } from 'react'

import ResetPasswordDialog from '@/components/users/reset-password-dialog'
import OrgUserAddressManager from '@/components/common/org-user-address-manager'
import { UserDocumentsPanel } from '@/components/profile/user-documents-panel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { getErrorMessage } from '@/lib/api'
import { getOrgUserGroupLabel } from '@/lib/org-user-groups'
import { filterSelectableBranchInstances } from '@/lib/branch-filters'
import { withSystemRoleCapabilityFallback } from '@/lib/route-access'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { authApi } from '@/services/auth'
import { branchesApi } from '@/services/branches'
import { orgTreeApi } from '@/services/org-tree'
import { orgUserAdminApi, type OrgUserAdminRecord, type OrgUserAssignment } from '@/services/org-user-admin'
import { useAuthStore } from '@/stores/auth-store'

const ORGANISATION_SCOPE = '__organisation__'
const NOT_PROVIDED_VALUE = '__not_provided__'

type EditUserFormState = {
  firstName: string
  lastName: string
  email: string
  phone: string
  dateOfBirth: string
  age: string
  gender: string
}

const getEditUserInitialState = (user: OrgUserAdminRecord): EditUserFormState => ({
  firstName: user.fname ?? '',
  lastName: user.lname ?? '',
  email: user.email ?? '',
  phone: user.phone ?? '',
  dateOfBirth: user.date_of_birth ?? '',
  age: user.age != null ? String(user.age) : '',
  gender: user.gender != null ? String(user.gender) : NOT_PROVIDED_VALUE,
})

const getAssignmentStatusLabel = (assignment: OrgUserAssignment): string => {
  if (assignment.is_selectable === false) {
    if (assignment.branch_status_label && assignment.branch_status_label !== 'Approved') {
      return `Unavailable (${assignment.branch_status_label} branch)`
    }
    return assignment.status_label || 'Unavailable'
  }

  return assignment.status_label || (assignment.is_active === false ? 'Suspended' : 'Approved')
}

const getAssignmentStatusBadgeClassName = (assignment: OrgUserAssignment): string => {
  if (assignment.is_selectable === false) {
    return ''
  }

  if (assignment.is_active === false || assignment.status_label === 'Pending') {
    return ''
  }

  return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
}

const normalizeCountryCode = (value?: string | null) => {
  const code = String(value ?? '').trim().toUpperCase()
  return /^[A-Z]{2}$/.test(code) ? code : ''
}

const resolveCountryLocale = (countryCode?: string | null) => {
  const normalized = normalizeCountryCode(countryCode)
  if (!normalized) {
    return 'en-US'
  }

  const candidates = [`${normalized.toLowerCase()}-${normalized}`, `en-${normalized}`, 'en-US']
  return Intl.DateTimeFormat.supportedLocalesOf(candidates)[0] || 'en-US'
}

const getUserName = (user?: { fname?: string | null; lname?: string | null; user_id?: number | string } | null) => {
  if (!user) {
    return ''
  }
  const name = [user.fname, user.lname].filter(Boolean).join(' ').trim()
  return name || String(user.user_id || '')
}

const getAddressUserId = (user?: { id?: number | string; user_id?: number | string } | null) => {
  if (!user) {
    return ''
  }

  const rawId = String(user.id ?? '').trim()
  if (rawId && Number.isNaN(Number(rawId))) {
    return rawId
  }
  return String(user.user_id ?? rawId)
}

const getGenderLabel = (value: number | null | undefined) => {
  switch (value) {
    case 0:
      return 'Male'
    case 1:
      return 'Female'
    case 2:
      return 'Other'
    default:
      return 'Not provided'
  }
}

const formatDateTime = (value?: string | null, locale = 'en-US') => {
  if (!value) {
    return 'Not available'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed)
}

const formatDateOnly = (value?: string | null, locale = 'en-US') => {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsed)
}

const normalizeInitialAddresses = (addresses?: OrgUserAdminRecord['addresses']) => {
  if (!addresses?.length) {
    return []
  }

  return addresses.map((address) => ({
    id: address.id,
    building_name: address.building_name ?? null,
    floor_line: address.address_line1 ?? null,
    street_line: address.address_line2 ?? null,
    zipcode: address.postal_code ?? null,
    door_no: address.door_no ?? null,
    city_name: address.city ?? null,
    state_name: address.state_name ?? null,
    country_name: address.country_name ?? null,
    country: address.country_id ?? null,
    state: address.state_id ?? null,
    default: Boolean(address.default),
  }))
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedBranchInstanceId = searchParams.get('branch_instance_id') || ''
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore((state) => state.user?.id)
  const currentPost = useAuthStore((state) => state.currentPost)
  const currentCapabilities = useAuthStore((state) => state.currentPost?.capabilities)
  const rolePermissionCapabilities = useMemo(() => {
    const permissions = currentPost?.roles_data?.permissions
    if (!permissions || typeof permissions !== 'object') {
      return undefined
    }

    const normalized: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(permissions as Record<string, unknown>)) {
      normalized[key] = value === true || value === 1 || String(value).toLowerCase() === 'true'
    }
    return normalized
  }, [currentPost?.roles_data?.permissions])
  const effectiveCapabilities = useMemo(() => {
    if (!currentCapabilities && !rolePermissionCapabilities) {
      return withSystemRoleCapabilityFallback(currentPost, undefined)
    }
    const mergedCapabilities = {
      ...(rolePermissionCapabilities ?? {}),
      ...(currentCapabilities ?? {}),
    }
    return withSystemRoleCapabilityFallback(currentPost, mergedCapabilities)
  }, [currentCapabilities, currentPost, rolePermissionCapabilities])
  const canUnlockUsers = Boolean(effectiveCapabilities?.can_unlock_users)
  const canForcePasswordResetUsers = Boolean(effectiveCapabilities?.can_force_password_reset_users)
  const canUpdateUsers = Boolean(effectiveCapabilities?.can_update_users)
  const canManageUserLifecycle = Boolean(effectiveCapabilities?.can_manage_user_lifecycle)
  const canAssignUserRoles = Boolean(effectiveCapabilities?.can_assign_user_roles)
  const canRemoveUserRoles = Boolean(effectiveCapabilities?.can_remove_user_roles)
  const canCreateUserAddresses = Boolean(effectiveCapabilities?.can_create_user_addresses)
  const canUpdateUserAddresses = Boolean(effectiveCapabilities?.can_update_user_addresses)
  const canRemoveUserAddresses = Boolean(effectiveCapabilities?.can_remove_user_addresses)
  const canSetDefaultUserAddresses = Boolean(effectiveCapabilities?.can_set_default_user_addresses)
  const canAccessUserDocuments = Boolean(effectiveCapabilities?.can_access_user_documents)
  const canVerifyUserDocuments = Boolean(effectiveCapabilities?.can_verify_user_documents)
  const canManageUserDocuments = Boolean(effectiveCapabilities?.can_manage_user_documents)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false)
  const [editForm, setEditForm] = useState<EditUserFormState | null>(null)
  const canViewUserDocumentsTab = canAccessUserDocuments
    || canManageUserDocuments
    || canManageUserLifecycle
    || canAssignUserRoles
    || canVerifyUserDocuments
  const effectiveScope = selectedBranchInstanceId || ORGANISATION_SCOPE
  const branchesQuery = useQuery({
    queryKey: ['organisation-branches', 'user-detail', serviceInstanceId],
    queryFn: () => branchesApi.list({
      serviceInstanceId: serviceInstanceId || undefined,
      uniqueLevelVariantId: 'branches',
    }),
    staleTime: 60_000,
  })
  const selectableBranches = useMemo(
    () => filterSelectableBranchInstances(branchesQuery.data ?? []),
    [branchesQuery.data],
  )

  const detailQuery = useQuery({
    queryKey: ['org-user-admin-detail', id, selectedBranchInstanceId],
    queryFn: () => orgUserAdminApi.get(id!, {
      branchInstanceId: selectedBranchInstanceId || undefined,
      serviceInstanceId: serviceInstanceId || undefined,
      orgInstanceId: serviceInstanceId || undefined,
    }),
    enabled: !!id,
  })

  const organizationQuery = useQuery({
    queryKey: ['org-user-detail-organization-service-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(String(serviceInstanceId)),
    enabled: Boolean(serviceInstanceId),
    staleTime: 5 * 60_000,
  })

  const removeAssignmentMutation = useMutation({
    mutationFn: (assignmentId: number) => orgUserAdminApi.removeAssignment(assignmentId),
    onSuccess: async () => {
      toast.success('Role assignment removed successfully')
      await authApi.refreshAuthState().catch(() => undefined)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['org-user-admin-detail', id] }),
        queryClient.invalidateQueries({ queryKey: ['org-user-admin'] }),
        queryClient.invalidateQueries({ queryKey: ['assigned-roles'] }),
      ])
      await queryClient.refetchQueries({ queryKey: ['org-user-admin-detail', id], type: 'active' })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const approveBranchAccessMutation = useMutation({
    mutationFn: (accessId: number | string) => orgUserAdminApi.approveBranchAssociation(accessId, serviceInstanceId || undefined),
    onSuccess: async () => {
      toast.success('Branch access approved successfully')
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin-detail', id] })
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const unlockMutation = useMutation({
    mutationFn: () => orgUserAdminApi.unlock(id!),
    onSuccess: async () => {
      toast.success('User account unlocked successfully')
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin-detail', id] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const resetPinMutation = useMutation({
    mutationFn: () => orgUserAdminApi.resetPin(id!),
    onSuccess: async () => {
      toast.success('User PIN reset successfully')
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin-detail', id] })
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const forcePasswordResetMutation = useMutation({
    mutationFn: (password: string) => orgUserAdminApi.forcePasswordReset(id!, serviceInstanceId || undefined, password),
    onSuccess: async () => {
      toast.success('Password reset successfully. The user must change it at next sign in.')
      setIsResetPasswordDialogOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin-detail', id] })
      await queryClient.invalidateQueries({ queryKey: ['org-users'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const lifecycleMutation = useMutation({
    mutationFn: async () => {
      const user = detailQuery.data
      if (!user) {
        throw new Error('User not loaded')
      }
      if (user.is_active === false) {
        return orgUserAdminApi.activate(user.id)
      }
      if (user.can_delete === false) {
        return orgUserAdminApi.suspend(user.id)
      }
      return orgUserAdminApi.remove(user.id)
    },
    onSuccess: async () => {
      const user = detailQuery.data
      if (user?.is_active === false) {
        toast.success('User reactivated successfully')
      } else if (user?.can_delete === false) {
        toast.success('User disabled successfully')
      } else {
        toast.success('User deleted successfully')
      }
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin-detail', id] })
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateUserMutation = useMutation({
    mutationFn: (form: EditUserFormState) => orgUserAdminApi.update(id!, {
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      email: form.email.trim(),
      phone_number: form.phone.trim(),
      date_of_birth: form.dateOfBirth.trim(),
      age: form.age.trim(),
      gender: form.gender === NOT_PROVIDED_VALUE ? '' : form.gender,
    }, {
      branchInstanceId: selectedBranchInstanceId || undefined,
      serviceInstanceId: serviceInstanceId || undefined,
      orgInstanceId: serviceInstanceId || undefined,
    }),
    onSuccess: async () => {
      toast.success('User updated successfully')
      setIsEditDialogOpen(false)
      setEditForm(null)
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin-detail', id] })
      await queryClient.invalidateQueries({ queryKey: ['org-user-admin'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const user = detailQuery.data
  const sortedAssignments = useMemo(() => {
    const assignments = [...(user?.assignments ?? [])]
    assignments.sort((a, b) => Number(b.id) - Number(a.id))
    return assignments
  }, [user?.assignments])
  const visibleAssignments = useMemo(
    () => sortedAssignments.filter((assignment) => assignment.is_selectable !== false),
    [sortedAssignments],
  )
  const selectableAssignmentCount = useMemo(() => {
    if (user?.selectable_role_assignment_count != null) {
      return user.selectable_role_assignment_count
    }

    return sortedAssignments.filter((assignment) => assignment.is_selectable !== false).length
  }, [sortedAssignments, user?.selectable_role_assignment_count])
  const organizationCountryCode = useMemo(
    () => normalizeCountryCode(organizationQuery.data?.country_code),
    [organizationQuery.data?.country_code],
  )
  const organizationDateLocale = useMemo(
    () => resolveCountryLocale(organizationCountryCode),
    [organizationCountryCode],
  )

  const handleBranchChange = (branchValue: string) => {
    const next = new URLSearchParams(searchParams)
    if (!branchValue || branchValue === ORGANISATION_SCOPE) {
      next.delete('branch_instance_id')
    } else {
      next.set('branch_instance_id', branchValue)
    }
    setSearchParams(next)
  }

  const openEditDialog = () => {
    if (!user) {
      return
    }
    setEditForm(getEditUserInitialState(user))
    setIsEditDialogOpen(true)
  }

  const updateEditForm = (key: keyof EditUserFormState, value: string) => {
    setEditForm((current) => current ? { ...current, [key]: value } : current)
  }

  const submitEditForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editForm) {
      return
    }
    updateUserMutation.mutate(editForm)
  }

  useEffect(() => {
    if (!selectedBranchInstanceId || branchesQuery.isLoading) {
      return
    }

    const hasSelectableBranch = selectableBranches.some(
      (branch) => String(branch.instanceId || branch.id) === selectedBranchInstanceId,
    )
    if (hasSelectableBranch) {
      return
    }

    const next = new URLSearchParams(searchParams)
    next.delete('branch_instance_id')
    setSearchParams(next, { replace: true })
  }, [branchesQuery.isLoading, searchParams, selectedBranchInstanceId, selectableBranches, setSearchParams])

  if (detailQuery.isLoading) {
    return <Skeleton className="h-72 w-full" />
  }

  if (detailQuery.error || !user) {
    return (
      <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 text-sm text-destructive">
        {detailQuery.error ? getErrorMessage(detailQuery.error) : 'User not found.'}
      </div>
    )
  }

  const addressUserId = getAddressUserId(user)
  const initialAddresses = normalizeInitialAddresses(user.addresses)
  const isSelfUser = String(user.user_id) === String(currentUserId ?? '')

  return (
    <div className="space-y-6 pb-2">
      <ResetPasswordDialog
        open={isResetPasswordDialogOpen}
        onOpenChange={setIsResetPasswordDialogOpen}
        subjectName={getUserName(user)}
        entityLabel="user"
        isPending={forcePasswordResetMutation.isPending}
        onConfirm={(password) => forcePasswordResetMutation.mutate(password)}
      />
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open)
        if (!open) {
          setEditForm(null)
        }
      }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update this user's profile and primary contact details.</DialogDescription>
          </DialogHeader>
          {editForm ? (
            <form className="space-y-4" onSubmit={submitEditForm}>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-2 text-sm font-medium">
                  First name
                  <Input value={editForm.firstName} onChange={(event) => updateEditForm('firstName', event.target.value)} required />
                </label>
                <label className="space-y-2 text-sm font-medium">
                  Last name
                  <Input value={editForm.lastName} onChange={(event) => updateEditForm('lastName', event.target.value)} />
                </label>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-2 text-sm font-medium">
                  Email
                  <Input type="email" value={editForm.email} onChange={(event) => updateEditForm('email', event.target.value)} />
                </label>
                <label className="space-y-2 text-sm font-medium">
                  Phone
                  <Input value={editForm.phone} onChange={(event) => updateEditForm('phone', event.target.value)} />
                </label>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="space-y-2 text-sm font-medium">
                  Date of birth
                  <Input type="date" value={editForm.dateOfBirth} onChange={(event) => updateEditForm('dateOfBirth', event.target.value)} />
                </label>
                <label className="space-y-2 text-sm font-medium">
                  Age
                  <Input inputMode="numeric" value={editForm.age} onChange={(event) => updateEditForm('age', event.target.value)} />
                </label>
                <label className="space-y-2 text-sm font-medium">
                  Gender
                  <Select value={editForm.gender} onValueChange={(value) => updateEditForm('gender', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NOT_PROVIDED_VALUE}>Not provided</SelectItem>
                      <SelectItem value="0">Male</SelectItem>
                      <SelectItem value="1">Female</SelectItem>
                      <SelectItem value="2">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={updateUserMutation.isPending}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateUserMutation.isPending}>
                  Save changes
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to={withServiceInstanceId(`/org/users${selectedBranchInstanceId ? `?branch_instance_id=${selectedBranchInstanceId}` : ''}`, serviceInstanceId)}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-foreground">{getUserName(user)}</h1>
          <p className="text-muted-foreground">User profile</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
        <Card className="rounded-[28px] border-border/80">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>User details</CardTitle>
                <CardDescription>Unlock, disable, reactivate, or remove this user from the organization.</CardDescription>
              </div>
              {canUpdateUsers ? (
                <Button type="button" variant="outline" size="sm" onClick={openEditDialog}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <UserRound className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-foreground">{getUserName(user)}</p>
                <p className="text-sm text-muted-foreground">{user.username || `User ${user.user_id}`}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge className={user.is_active === false ? 'bg-slate-100 text-slate-700' : 'bg-emerald-100 text-emerald-700'}>
                {user.is_active === false ? 'Inactive' : 'Active'}
              </Badge>
              {user.is_locked ? <Badge variant="outline">Locked</Badge> : null}
              {user.force_password_reset_pending ? (
                <Badge className="bg-amber-100 text-amber-900 border-amber-200">
                  Password reset required until {formatDateTime(user.force_password_reset_expires_at, organizationDateLocale)}
                </Badge>
              ) : null}
              <Badge variant="outline">{getOrgUserGroupLabel(user.user_group_title, 'User')}</Badge>
            </div>
            <div className="grid gap-3 text-sm text-muted-foreground">
              <div>Registration Number: <span className="font-mono text-foreground">{user.usr_reg_num || '—'}</span></div>
              <div>Email: {user.email || 'No email'}</div>
              <div>Phone: {user.phone || 'No phone'}</div>
              <div>Date of birth: {user.date_of_birth || 'Not provided'}</div>
              <div>Age: {user.age != null ? user.age : 'Not provided'}</div>
              <div>Gender: {getGenderLabel(user.gender)}</div>
              <div>Exposure notice acknowledged: {user.exposure_notice_acknowledged_on ? 'Yes' : 'No'}</div>
              <div>Password reset pending: {user.force_password_reset_pending ? 'Yes' : 'No'}</div>
              {user.force_password_reset_pending ? (
                <div>Password reset expires: {formatDateTime(user.force_password_reset_expires_at, organizationDateLocale)}</div>
              ) : null}
              <div>
                Primary address: {user.address_data
                  ? [user.address_data.city, user.address_data.state_name, user.address_data.country_name].filter(Boolean).join(', ') || 'Saved'
                  : 'No address'}
              </div>
              <div>Linked expenses: {user.linked_expense_count ?? 0}</div>
              <div>
                Linked role assignments: {user.linked_role_assignment_count ?? 0}
                {(user.linked_role_assignment_count ?? 0) !== selectableAssignmentCount ? (
                  <span className="text-muted-foreground">
                    {' '}({selectableAssignmentCount} selectable at sign-in)
                  </span>
                ) : null}
              </div>
              <div>Linked worker profiles: {user.linked_worker_count ?? 0}</div>
            </div>
            <div className="flex flex-wrap gap-3">
              {canUnlockUsers ? (
                <Button variant="outline" onClick={() => unlockMutation.mutate()} disabled={unlockMutation.isPending || !user.is_locked || isSelfUser}>
                  <Unlock className="mr-2 h-4 w-4" />
                  Unlock account
                </Button>
              ) : null}
              {canUnlockUsers ? (
                <Button variant="outline" onClick={() => resetPinMutation.mutate()} disabled={resetPinMutation.isPending || isSelfUser}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  Reset PIN
                </Button>
              ) : null}
              {canForcePasswordResetUsers ? (
                <Button
                  variant="outline"
                  onClick={() => setIsResetPasswordDialogOpen(true)}
                  disabled={forcePasswordResetMutation.isPending || isSelfUser}
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  Reset password
                </Button>
              ) : null}
              {canManageUserLifecycle ? (
                <Button variant="outline" onClick={() => lifecycleMutation.mutate()} disabled={lifecycleMutation.isPending || isSelfUser}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  {user.is_active === false ? 'Reactivate user' : user.can_delete === false ? 'Disable user' : 'Delete user'}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[28px] border-border/80">
          <Tabs defaultValue="branch-roles">
            <CardHeader>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle>User profile</CardTitle>
                  <CardDescription>Review assigned roles, addresses, and documents. Assign new roles from the Roles page.</CardDescription>
                </div>
                <TabsList>
                  <TabsTrigger value="branch-roles">
                    Branch roles{visibleAssignments.length ? ` (${visibleAssignments.length})` : ''}
                  </TabsTrigger>
                  <TabsTrigger value="addresses">Addresses</TabsTrigger>
                  {canViewUserDocumentsTab ? <TabsTrigger value="documents">Documents</TabsTrigger> : null}
                </TabsList>
              </div>
            </CardHeader>
            <CardContent>
              <TabsContent value="branch-roles" className="mt-0 space-y-4">
                <div className="max-w-md">
                  <Select value={effectiveScope} onValueChange={handleBranchChange}>
                    <SelectTrigger aria-label="Role scope">
                      <SelectValue placeholder="Select scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ORGANISATION_SCOPE}>Organisation</SelectItem>
                      {(selectableBranches).map((branch) => (
                        <SelectItem key={String(branch.id)} value={String(branch.instanceId || branch.id)}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="overflow-hidden rounded-2xl border border-border/70 bg-background">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableHead className="pl-5">Role</TableHead>
                        <TableHead>Branch</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="pr-5 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleAssignments.length ? visibleAssignments.map((assignment) => (
                        <TableRow key={assignment.id}>
                          <TableCell className="pl-5">
                            <div>
                              <p className="font-medium text-foreground">{assignment.role_name || 'Role'}</p>
                              <p className="text-xs text-muted-foreground">
                                {assignment.is_business_scope ? 'Organisation role' : assignment.service_variant_name || 'Service branch role'}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {assignment.is_business_scope ? 'Organisation' : assignment.branch_name || assignment.branch_instance_id || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={assignment.is_selectable === false || assignment.is_active === false ? 'destructive' : 'default'}
                              className={getAssignmentStatusBadgeClassName(assignment)}
                            >
                              {getAssignmentStatusLabel(assignment)}
                            </Badge>
                          </TableCell>
                          <TableCell className="pr-5 text-right">
                            {canRemoveUserRoles ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Role actions"
                                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  >
                                    <EllipsisVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => removeAssignmentMutation.mutate(assignment.id)}
                                    disabled={removeAssignmentMutation.isPending}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Remove role
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">
                            No roles assigned for the current selection.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <Card className="rounded-2xl border-border/70 shadow-none">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Branch access requests</CardTitle>
                    <CardDescription>Approve pending branch join requests for this user.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-hidden rounded-2xl border border-border/70 bg-background">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableHead className="pl-5">Branch</TableHead>
                            <TableHead>Requested</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="pr-5 text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(user.visibility_accesses ?? []).length ? (user.visibility_accesses ?? []).map((access) => (
                            <TableRow key={String(access.access_id ?? access.id ?? access.instance_id)}>
                              <TableCell className="pl-5 font-medium text-foreground">{access.name || access.instance_id || '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{formatDateOnly(access.created_on, organizationDateLocale)}</TableCell>
                              <TableCell>
                                <Badge variant={access.status_label === 'Pending' ? 'outline' : 'default'}>{access.status_label || 'Approved'}</Badge>
                              </TableCell>
                              <TableCell className="pr-5 text-right">
                                {canAssignUserRoles && access.status_label === 'Pending' && access.access_id ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => approveBranchAccessMutation.mutate(access.access_id as number)}
                                    disabled={approveBranchAccessMutation.isPending}
                                  >
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Approve
                                  </Button>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          )) : (
                            <TableRow>
                              <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">
                                No branch access requests for this user.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {canViewUserDocumentsTab ? (
                <TabsContent value="documents" className="mt-0">
                  <UserDocumentsPanel
                    targetUserId={user.user_id}
                    orgInstanceId={serviceInstanceId ?? undefined}
                    userGroupId={user.user_group ?? undefined}
                    allowVerifyControls={(canVerifyUserDocuments || canManageUserDocuments || canManageUserLifecycle || canAssignUserRoles) && !isSelfUser}
                  />
                </TabsContent>
              ) : null}

              <TabsContent value="addresses" className="mt-0">
                {addressUserId ? (
                  <OrgUserAddressManager
                    userId={addressUserId}
                    entityDisplayName={getUserName(user)}
                    email={user.email ?? null}
                    phone={user.phone ?? null}
                    initialAddresses={initialAddresses}
                    canCreateAddresses={canCreateUserAddresses}
                    canUpdateAddresses={canUpdateUserAddresses}
                    canDeleteAddresses={canRemoveUserAddresses}
                    canSetDefaultAddresses={canSetDefaultUserAddresses}
                    entityLabel="user"
                    detailQueryKey={['org-user-admin-detail', id, selectedBranchInstanceId]}
                  />
                ) : (
                  <div className="rounded-xl border border-dashed border-border/70 bg-muted/10 px-4 py-6 text-sm text-muted-foreground">
                    Address information cannot be loaded because the user id is missing.
                  </div>
                )}
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>
    </div>
  )
}